import { type CompanyEmployeeDetailsDto } from '@/api/generated/models/company-employee-details-dto';
import { type DictionaryEnumDto } from '@/api/generated/models/dictionary-enum-dto';
import { DictionaryEnumTypeDto } from '@/api/generated/models/dictionary-enum-type-dto';
import { RoleTypeDto } from '@/api/generated/models/role-type-dto';
import { type UserStatusDto } from '@/api/generated/models/user-status-dto';
import { useDictionaryTranslation } from '@/common/useDictionaryTranslation.ts';
import { type MyProfileModalFormModel } from '@/components/company/companyUserModal/model/myProfileModalForm.model.ts';
import { type SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';

type Return = {
  allOperatingLocations: SelectSearchOptionModel<number>[];
  initialFormValue: MyProfileModalFormModel;
  rolesOptions: SelectSearchOptionModel[];
  statusesOptions: SelectSearchOptionModel[];
};

export const useMyProfileModalFormInit = (
  profile: CompanyEmployeeDetailsDto,
  currentUserRole?: RoleTypeDto,
): Return => {
  const { dictionaryEnums, locationsOptions } = useDictionariesContext();
  const { dt } = useDictionaryTranslation();
  const {
    firstName,
    lastName,
    locationId,
    operatingLocations: userOperatingLocations,
    phoneNumber,
    role: userRole,
    status: userStatus,
  } = profile;

  if (!currentUserRole) {
    throw new Error('User role is required');
  }

  let rolesOptions: SelectSearchOptionModel<RoleTypeDto>[] = getEnumOptions(
    dictionaryEnums,
    DictionaryEnumTypeDto.UserRole,
    'defaultRoles',
    dt,
  );
  if (currentUserRole !== RoleTypeDto.Admin) {
    rolesOptions = rolesOptions.filter(role => role.value !== RoleTypeDto.Admin);
  }
  const statusesOptions: SelectSearchOptionModel<UserStatusDto>[] = getEnumOptions(
    dictionaryEnums,
    DictionaryEnumTypeDto.UserStatus,
    'employeeStatuses',
    dt,
  );

  const location = locationId
    ? locationsOptions.find(option => option.value === locationId)
    : undefined;

  const status = getSelectSearchOption<UserStatusDto>(statusesOptions, userStatus);
  const role = getSelectSearchOption<RoleTypeDto>(rolesOptions, userRole);

  const operatingLocations: SelectSearchOptionModel<number>[] = userOperatingLocations
    .map(entry => locationsOptions.find(option => option.value === entry))
    .filter((it): it is SelectSearchOptionModel<number> => Boolean(it?.value));

  return {
    allOperatingLocations: locationsOptions,
    initialFormValue: {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      location,
      operatingLocations,
      phoneNumber: phoneNumber ?? undefined,
      role,
      status,
    },
    rolesOptions,
    statusesOptions,
  };
};

const getEnumOptions = <ReturnType extends number | string>(
  values: DictionaryEnumDto[],
  type: DictionaryEnumTypeDto,
  translationName: string,
  dt: (key: string, value: string) => string,
): SelectSearchOptionModel<ReturnType>[] => {
  const items = values.find(enumList => enumList.type === type)?.values ?? [];

  return items.map(item => ({
    label: dt(translationName, item),
    value: item as ReturnType,
  }));
};

const getSelectSearchOption = <T extends number | string>(
  options: SelectSearchOptionModel<T>[],
  lookedFor: string | undefined,
): SelectSearchOptionModel<T> | undefined => {
  const found = options.find(entry => entry.value === lookedFor);
  return found ?? undefined;
};
